import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  GetFinanceOverviewDataAsync,
  SendReminderToProjectManagersAsync,
  ToggleUserNotificationsAsync,
} from "../services/FinanceService";
import { FinanceOverviewItem } from "../types/FinanceOverviewItem";
import { useTimePeriodContext } from "../contexts/TimePeriodContext";

export function useFinance() {
  const { instance, account, user, authLoading } = useAuth();
  const { selectedPeriod } = useTimePeriodContext();

  const [financeOverviews, setFinanceOverviews] = useState<FinanceOverviewItem[]>([]);

  const [actionloading, setActionLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);

  const fetchFinanceOverviews = useCallback(async () => {
    setDataLoading(true);
    setFetchError(false);

    if (authLoading) return;

    try {
      const overviews = await GetFinanceOverviewDataAsync(instance, account, selectedPeriod);

      if (!overviews) throw new Error();

      setFinanceOverviews(overviews);
    } catch {
      setFetchError(true);
    } finally {
      setDataLoading(false);
    }
  }, [instance, account, selectedPeriod, authLoading]);

  useEffect(() => {
    fetchFinanceOverviews();
  }, [fetchFinanceOverviews]);

  const toggleNotifs = useCallback(async () => {
    if (!user?.id) return;
    setActionLoading(true);
    try {
      await ToggleUserNotificationsAsync(instance, account, user.id);
      user.hasNotificationsOn = !user.hasNotificationsOn;
    } catch (error) {
      console.error("Failed to toggle notifications", error);
    } finally {
      setActionLoading(false);
    }
  }, [instance, account, user]);

  const sendReminder = useCallback(async () => {
    setActionLoading(true);
    setShowConfirmPopup(false);

    try {
      await SendReminderToProjectManagersAsync();
    } catch (error) {
      console.error("Failed to send notifications", error);
    } finally {
      setActionLoading(false);
    }
  }, []);

  return {
    fetchFinanceOverviews,
    financeOverviews,
    toggleNotifs,
    sendReminder,
    actionloading,
    dataLoading,
    fetchError,
    showConfirmPopup,
    openConfirmPopup: () => setShowConfirmPopup(true),
    closeConfirmPopup: () => setShowConfirmPopup(false),
  };
}
